import React, { useCallback, useContext } from 'react';
import type { TrackingDataV2 } from '@oneaudi/audi-tracking-service';
import { useFeatureAppEnv } from '../context/FeatureAppEnvContext';
import { CompareContext, type CompareCarline } from '../context/CompareContext';

export interface CompareTrackOptions {
  readonly carline: CompareCarline;
}

export interface AddOptions extends CompareTrackOptions {
  readonly label: string;
}

export interface RemoveOptions extends CompareTrackOptions {
  readonly label: string;
  readonly elementName: 'check box' | 'text link';
}

export interface RemoveAllOptions {
  readonly label: string;
}

export interface ToggleStickyBarOptions {
  readonly open: boolean;
}

export interface CompareOptions {
  readonly label: string;
  readonly targetURL: string;
}

export interface CompareTrackingManager {
  readonly add: (options: AddOptions) => void;
  readonly remove: (options: RemoveOptions) => void;
  readonly autoRemove: (options: CompareTrackOptions) => void;
  readonly removeAll: (options: RemoveAllOptions) => void;
  readonly toggleStickyBar: (options: ToggleStickyBarOptions) => void;
  readonly compare: (options: CompareOptions) => void;
}

const createCompareTrackingPayload = (
  action: 'content' | 'internal_link',
  name: string,
  amountOfCarsSelected: number,
  attributes: Record<string, unknown>,
): TrackingDataV2 => {
  const defaultAttributes = {
    componentName: 'model-selector',
    clickID: '',
    pos: amountOfCarsSelected.toString(10),
    targetURL: '',
  };

  return {
    event: {
      eventInfo: {
        eventAction: action,
        eventName: `model selector - ${name}`,
      },
      attributes: {
        ...defaultAttributes,
        ...attributes,
      },
    },
  };
};

const mapCompareCarlineToComparedVehicle = ({ id, name, group }: CompareCarline) => ({
  productInfo: {
    productID: id,
    productName: name,
    manufacturer: 'Audi',
  },
  category: {
    primaryCategory: group,
    subCategory1: id,
    productType: 'compared car',
  },
});

const createValueFromCompareCarline = ({ name, group }: CompareCarline) => `${group}~${name}`;

export const useCompareTrackingManager = (): CompareTrackingManager => {
  const { 's2:logger': logger, 'audi-tracking-service': trackingService } =
    useFeatureAppEnv().featureServices;
  const { state } = useContext(CompareContext);
  const track = useCallback(
    (...args: Parameters<typeof createCompareTrackingPayload>): void => {
      if (trackingService) {
        const payload = createCompareTrackingPayload(...args);
        logger.debug('Sending tracking event: ', JSON.stringify(payload, null, 2));
        trackingService.track(payload);
      }
    },
    [logger, trackingService],
  );

  return React.useMemo(() => {
    const add = ({ carline, label }: AddOptions) => {
      track('content', 'click to add vehicle to comparison', state.carlines.length, {
        elementName: 'check box',
        value: createValueFromCompareCarline(carline),
        label,
        relatedProducts: {
          comparedVehicle: [mapCompareCarlineToComparedVehicle(carline)],
        },
      });
    };

    const remove = ({ carline, label, elementName }: RemoveOptions) => {
      track('content', 'click to remove vehicle from comparison', state.carlines.length, {
        elementName,
        value: createValueFromCompareCarline(carline),
        label,
        actionType: 'click',
        relatedProducts: {
          comparedVehicle: [mapCompareCarlineToComparedVehicle(carline)],
        },
      });
    };

    const autoRemove = ({ carline }: CompareTrackOptions) => {
      track('content', 'click to remove vehicle from comparison', state.carlines.length, {
        elementName: '',
        value: createValueFromCompareCarline(carline),
        label: '',
        actionType: 'automatic',
        relatedProducts: {
          comparedVehicle: [mapCompareCarlineToComparedVehicle(carline)],
        },
      });
    };

    const removeAll = ({ label }: RemoveAllOptions) => {
      track('content', 'click to remove all vehicles', state.carlines.length, {
        elementName: 'text link',
        value: '',
        label,
        actionType: 'click',
        relatedProducts: {
          comparedVehicle: state.carlines.map(mapCompareCarlineToComparedVehicle),
        },
      });
    };

    const toggleStickyBar = ({ open }: ToggleStickyBarOptions) => {
      track(
        'content',
        `click to ${open ? 'open' : 'close'} comparison bar`,
        state.carlines.length,
        {
          elementName: 'other',
          value: '',
          label: '',
          relatedProducts: {
            comparedVehicle: state.carlines.map(mapCompareCarlineToComparedVehicle),
          },
        },
      );
    };

    const compare = ({ label, targetURL }: CompareOptions) => {
      track('internal_link', 'click on comparison', state.carlines.length, {
        elementName: 'button',
        label,
        targetURL,
        value: '',
        relatedProducts: {
          comparedVehicle: state.carlines.map(mapCompareCarlineToComparedVehicle),
        },
      });
    };

    return {
      add,
      remove,
      autoRemove,
      removeAll,
      toggleStickyBar,
      compare,
    };
  }, [track, state]);
};
